import {
  addDoc,
  collection,
  collectionGroup,
  deleteDoc,
  deleteField,
  doc,
  getCountFromServer,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  startAfter,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "./firebase";
import { CONTACT_FILTERS } from "../utils/staffNotesPresentation";

// Staff notes (spec 059): users/{personUid}/staffNotes/{noteId}, private to staff — the person
// never reads them. The rules let every staff member read and add, the author edit and delete
// their own, and an admin delete any. Field names here are the rules' exactly. Errors are
// rethrown, never swallowed, like the other services.

export const NOTE_MAX = 2000;
export const NOTES_PAGE_SIZE = 20;
const AUTHOR_NAME_MAX = 100;

const notesOf = (uid) => collection(db, "users", uid, "staffNotes");
const noteRef = (uid, noteId) => doc(db, "users", uid, "staffNotes", noteId);

// Reads one past the page so hasMore is exact (dataService's pageOf rule).
const pageOf = (snap, pageSize, row) => {
  const page = snap.docs.slice(0, pageSize);
  return {
    rows: page.map(row),
    cursor: page.length ? page[page.length - 1] : null,
    hasMore: snap.docs.length > pageSize,
  };
};

// One page of a person's notes, newest first, in usePagedQuery's shape: { rows, cursor, hasMore }.
export const getStaffNotesPage = async (uid, { cursor = null, pageSize = NOTES_PAGE_SIZE } = {}) => {
  const parts = [notesOf(uid), orderBy("createdAt", "desc")];
  if (cursor) parts.push(startAfter(cursor));
  parts.push(limit(pageSize + 1));
  return pageOf(await getDocs(query(...parts)), pageSize, (d) => ({ id: d.id, ...d.data() }));
};

// The Notes tab's count pill.
export const countStaffNotes = async (uid) => (await getCountFromServer(notesOf(uid))).data().count;

// `fields` is noteFields()' shape (D8): { type: "general", text } or { type: "contact", text,
// replied, interested? }. `author` is the signed-in staff member (AuthContext's merged user): the
// uid, and the staff document's name, else the email.
export const addStaffNote = (uid, fields, author) =>
  addDoc(notesOf(uid), {
    ...fields,
    authorUid: author.uid,
    authorName: String(author.name || author.email || "").slice(0, AUTHOR_NAME_MAX),
    createdAt: serverTimestamp(),
  });

// The author's edit: the text, and on a contact note the reply and the interest. The type never
// changes. Setting replied to false removes `interested`, as the rules require.
export const updateStaffNote = (uid, noteId, { type, ...fields }) => {
  const change = { ...fields, updatedAt: serverTimestamp() };
  if (fields.replied === false) change.interested = deleteField();
  return updateDoc(noteRef(uid, noteId), change);
};

export const deleteStaffNote = (uid, noteId) => deleteDoc(noteRef(uid, noteId));

// The date range (D19) as rangeFor() gives it, { from, to } Dates with null for an open end. A
// range on createdAt, the orderBy field, needs no index beyond the ones below.
const rangeClauses = ({ from = null, to = null } = {}) => [from && where("createdAt", ">=", from), to && where("createdAt", "<=", to)].filter(Boolean);

// The Contact report (D11): every contact note across every student, newest first, under one of
// CONTACT_FILTERS. The count takes the same shape, orderBy included, so both run on the same
// collection-group indexes (type+createdAt, type+replied+createdAt, type+interested+createdAt).
const contactQuery = (filterKey, range, ...extra) => {
  const filter = CONTACT_FILTERS.find((f) => f.key === filterKey) || CONTACT_FILTERS[0];
  const parts = [collectionGroup(db, "staffNotes"), where("type", "==", "contact")];
  if (filter.where) parts.push(where(filter.where[0], "==", filter.where[1]));
  parts.push(...rangeClauses(range), orderBy("createdAt", "desc"), ...extra);
  return query(...parts);
};

// Each row carries `studentId`, the owner of the subcollection the note sits in.
export const getContactNotesPage = async (filterKey, { cursor = null, pageSize = NOTES_PAGE_SIZE, range } = {}) => {
  const extra = cursor ? [startAfter(cursor), limit(pageSize + 1)] : [limit(pageSize + 1)];
  const snap = await getDocs(contactQuery(filterKey, range, ...extra));
  return pageOf(snap, pageSize, (d) => ({ id: d.id, studentId: d.ref.parent.parent.id, ...d.data() }));
};

export const countContactNotes = async (filterKey, range) => (await getCountFromServer(contactQuery(filterKey, range))).data().count;

// Search mode (D18): one student's contact notes in the range, newest first, on the
// subcollection's type+createdAt index. The page merges several students' lists and filters
// them in memory, so there is no reply/interest clause here.
export const SEARCH_NOTES_MAX = 50;
export const getStudentContactNotes = async (uid, range) => {
  const snap = await getDocs(query(notesOf(uid), where("type", "==", "contact"), ...rangeClauses(range), orderBy("createdAt", "desc"), limit(SEARCH_NOTES_MAX)));
  return snap.docs.map((d) => ({ id: d.id, studentId: uid, ...d.data() }));
};
